import {
  Button as ChakraUIButton,
  ButtonGroup,
  ButtonProps,
  ComponentStyleConfig
} from "@chakra-ui/react";
import { ReactNode } from "react";

interface IButtonProps extends ButtonProps {
  // new prop added to implement destructive variant to buttons
  isDestructive?: boolean;
  children?: ReactNode;
}

const ButtonComponentStyle: ComponentStyleConfig = {
  variants: {
    primary: {
      variant: "solid",
      backgroundColor: "brand.500",
      _hover: {
        backgroundColor: "brand.700 !important"
      },
      _active: {
        backgroundColor: "brand.800 !important"
      },
      _disabled: { opacity: "0.5" },
      color: "white"
    },
    secondary: {
      variant: "outline",
      backgroundColor: "white",
      color: "gray.800",
      borderWidth: 1,
      borderStyle: "solid",
      borderColor: "gray.800",
      _hover: { backgroundColor: "brand.50" },
      _active: { backgroundColor: "brand.100" },
      _disabled: {
        color: "gray.800",
        opacity: "0.5"
      }
    },
    tertiary: {
      variant: "ghost",
      backgroundColor: "white",
      color: "gray.800",
      _hover: { backgroundColor: "gray.100" },
      _active: { backgroundColor: "gray.200" },
      _disabled: { opacity: "0.5" }
    },
    "emoji-row": {
      variant: "ghost",
      backgroundColor: "white",
      background: "white",
      borderLeftWidth: 4,
      borderLeftStyle: "solid",
      borderLeftColor: "transparent",
      _hover: {
        background: "white",
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        "@media (hover: hover) and (pointer: fine)": {
          svg: {
            color: "yellow.200"
          },
          p: {
            fontWeight: "semibold"
          }
        }
      },
      _focus: {
        background: "white",
        svg: {
          color: "yellow.200"
        },
        p: {
          fontWeight: "semibold"
        }
      },
      _active: {
        background: "white",
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        borderLeftColor: "brand.500",
        borderBottomColor: "transparent",
        svg: {
          color: "yellow.300"
        },
        p: {
          fontWeight: "bold"
        }
      }
    },
    "emoji-column": {
      variant: "ghost",
      backgroundColor: "white",
      background: "white",
      borderBottomWidth: 4,
      borderBottomStyle: "solid",
      borderBottomColor: "transparent",
      _hover: {
        background: "white",
        borderTopLeftRadius: "md",
        borderBottomRightRadius: 0,
        "@media (hover: hover) and (pointer: fine)": {
          svg: {
            color: "yellow.200"
          },
          p: {
            fontWeight: "semibold"
          }
        }
      },
      _focus: {
        background: "white",
        svg: {
          color: "yellow.200"
        },
        p: {
          fontWeight: "semibold"
        }
      },
      _active: {
        background: "white",
        borderTopLeftRadius: "md",
        borderBottomRightRadius: 0,
        borderBottomLeftRadius: 0,
        borderLeftColor: "transparent",
        borderBottomColor: "brand.500",
        svg: {
          color: "yellow.300"
        },
        p: {
          fontWeight: "bold"
        }
      }
    },
    link: {
      variant: "link",
      color: "brand.500",
      textDecoration: "underline",
      textUnderlineOffset: "2px",
      _disabled: { opacity: "0.5" }
    },
    "destructive-primary": {
      variant: "solid",
      backgroundColor: "red.600",
      color: "white",
      _hover: { backgroundColor: "red.800" },
      _active: { backgroundColor: "red.900" },
      _disabled: { opacity: "0.5" }
    },
    "destructive-secondary": {
      variant: "outline",
      backgroundColor: "white",
      color: "red.600",
      borderWidth: 1,
      borderStyle: "solid",
      borderColor: "red.600",
      _hover: { backgroundColor: "red.50" },
      _active: { backgroundColor: "red.100" },
      _disabled: { opacity: "0.5" }
    },
    "destructive-tertiary": {
      variant: "ghost",
      backgroundColor: "white",
      color: "red.600",
      _hover: { backgroundColor: "red.100" },
      _active: { backgroundColor: "red.200" },
      _disabled: { opacity: "0.5" }
    },
    "destructive-link": {
      variant: "link",
      backgroundColor: "white",
      color: "red.600",
      textDecoration: "underline",
      textUnderlineOffset: "2px",
      _disabled: { opacity: "0.5" }
    }
  }
};

function Button({ children, ...props }: IButtonProps) {
  return (
    <ChakraUIButton
      {...props}
      variant={
        props.isDestructive ? `destructive-${props.variant}` : props.variant
      }
    >
      <span>{children}</span>
    </ChakraUIButton>
  );
}

export { ButtonComponentStyle, Button, ButtonGroup, type IButtonProps };
